/**
 * PolicyConfigTabs — Reusable policy configuration tabs
 * Extracted from InstanceConfig.tsx for use in both instance-specific
 * and team-default policy editing contexts.
 */

import { useState } from 'react';

// ── Channel Key type (exported for use in Settings.tsx) ──

export interface ChannelKey {
	key_id: string;
	channel_type: string;
	label: string;
	is_default: boolean;
	assigned_to: { agent_id: string; hostname: string; instance_id?: string; instance_name?: string } | null;
}

export interface TeamAgentInstance {
	instance_id: string;
	instance_name: string;
	running_status: string;
}

export interface TeamAgent {
	agent_id: string;
	hostname: string;
	status: 'online' | 'offline';
	instances?: TeamAgentInstance[];
}

// ── Integration types ──

interface TelegramIntegration {
	enabled: boolean;
	bot_token: string;
	allowed_chat_ids: string[];
	webhook_url?: string;
}

interface SlackIntegration {
	enabled: boolean;
	bot_token: string;
	app_token: string;
	signing_secret: string;
	allowed_channels: string[];
}

interface DiscordIntegration {
	enabled: boolean;
	bot_token: string;
	allowed_guild_ids: string[];
	allowed_channel_ids: string[];
}

interface WebhookIntegration {
	enabled: boolean;
	endpoint_url: string;
	secret_key: string;
	allowed_origins: string[];
}

interface LocalPortIntegration {
	enabled: boolean;
	port: number;
	bind_address: string;
	require_auth: boolean;
	auth_token?: string;
}

interface EmailIntegration {
	enabled: boolean;
	imap_host: string;
	imap_port: number;
	smtp_host: string;
	smtp_port: number;
	email_address: string;
	password: string;
	use_tls: boolean;
	allowed_senders: string[];
}

interface WechatIntegration {
	enabled: boolean;
	app_id: string;
	app_secret: string;
	token: string;
	encoding_aes_key?: string;
}

interface WhatsappIntegration {
	enabled: boolean;
	phone_number_id: string;
	access_token: string;
	business_account_id: string;
	verify_token?: string;
}

interface ImessageIntegration {
	enabled: boolean;
	business_id: string;
	msp_id: string;
	api_key: string;
}

export interface IntegrationsConfig {
	telegram: TelegramIntegration;
	slack: SlackIntegration;
	discord: DiscordIntegration;
	webhook: WebhookIntegration;
	local_port: LocalPortIntegration;
	email: EmailIntegration;
	wechat: WechatIntegration;
	whatsapp: WhatsappIntegration;
	imessage: ImessageIntegration;
}

// ── OAuth types ──

interface OAuthProvider {
	enabled: boolean;
	client_id: string;
	client_secret: string;
	allowed_domains: string[];
	allowed_emails: string[];
}

export interface OAuthConfig {
	require_oauth: boolean;
	google: OAuthProvider;
	github: OAuthProvider;
	microsoft: OAuthProvider;
}

// ── AI Provider types ──

export type AIProviderName = 'anthropic' | 'openai' | 'google_ai' | 'openrouter';

export interface AIProviderConfig {
	enabled: boolean;
	is_configured: boolean;
	allowed_models: string[];
	last_verified?: number;
	endpoint?: string;
}

export interface AIProvidersConfig {
	anthropic: AIProviderConfig;
	openai: AIProviderConfig;
	google_ai: AIProviderConfig;
	openrouter: AIProviderConfig;
}

// ── Threat / PAM types ──

type ThreatActionValue = 'block' | 'request' | 'ignore' | 'log';

interface ThreatActionsConfig {
	critical: ThreatActionValue;
	high: ThreatActionValue;
	medium: ThreatActionValue;
	low: ThreatActionValue;
}

type PAMActionValue = 'block' | 'mfa_challenge' | 'allow';

interface PAMScopeConfig {
	enabled: boolean;
	action: PAMActionValue;
}

interface PAMScopesConfig {
	destructive: PAMScopeConfig;
	credential: PAMScopeConfig;
	system_admin: PAMScopeConfig;
	code_execution: PAMScopeConfig;
}

interface PAMRiskActionsConfig {
	critical: PAMActionValue;
	high: PAMActionValue;
	medium: PAMActionValue;
	low: PAMActionValue;
}

// ── Section overrides ──

export interface SectionOverrides {
	security?: boolean;
	ai_providers?: boolean;
	integrations?: boolean;
}

// ── PolicyConfig ──

export interface PolicyConfig {
	allowed_models: string[];
	blocked_models: string[];
	enable_threat_detection: boolean;
	blocked_patterns: string[];
	threat_actions?: ThreatActionsConfig;
	enable_pam?: boolean;
	pam_scopes?: PAMScopesConfig;
	pam_risk_actions?: PAMRiskActionsConfig;
	mfa_challenge_timeout_seconds?: number;
	// Fields below are stored but not yet enforced in the agent.
	// Kept in the interface for backward-compat with existing policy data.
	log_all_requests: boolean;
	log_responses: boolean;
	alert_on_threats: boolean;
	require_request_signing: boolean;
	allowed_ip_ranges: string[];
	integrations: IntegrationsConfig;
	oauth: OAuthConfig;
	ai_providers?: AIProvidersConfig;
	provider_order?: AIProviderName[];
	section_overrides?: SectionOverrides;
}

// ── Defaults ──

export const DEFAULT_INTEGRATIONS: IntegrationsConfig = {
	telegram: { enabled: false, bot_token: '', allowed_chat_ids: [] },
	slack: { enabled: false, bot_token: '', app_token: '', signing_secret: '', allowed_channels: [] },
	discord: { enabled: false, bot_token: '', allowed_guild_ids: [], allowed_channel_ids: [] },
	webhook: { enabled: false, endpoint_url: '', secret_key: '', allowed_origins: [] },
	local_port: { enabled: false, port: 8080, bind_address: '127.0.0.1', require_auth: true, auth_token: '' },
	email: { enabled: false, imap_host: '', imap_port: 993, smtp_host: '', smtp_port: 587, email_address: '', password: '', use_tls: true, allowed_senders: [] },
	wechat: { enabled: false, app_id: '', app_secret: '', token: '' },
	whatsapp: { enabled: false, phone_number_id: '', access_token: '', business_account_id: '' },
	imessage: { enabled: false, business_id: '', msp_id: '', api_key: '' },
};

export const DEFAULT_OAUTH: OAuthConfig = {
	require_oauth: false,
	google: { enabled: false, client_id: '', client_secret: '', allowed_domains: [], allowed_emails: [] },
	github: { enabled: false, client_id: '', client_secret: '', allowed_domains: [], allowed_emails: [] },
	microsoft: { enabled: false, client_id: '', client_secret: '', allowed_domains: [], allowed_emails: [] },
};

export const DEFAULT_AI_PROVIDERS: AIProvidersConfig = {
	anthropic: { enabled: false, is_configured: false, allowed_models: [] },
	openai: { enabled: false, is_configured: false, allowed_models: [] },
	google_ai: { enabled: false, is_configured: false, allowed_models: [] },
	openrouter: { enabled: false, is_configured: false, allowed_models: [] },
};

export const DEFAULT_PROVIDER_ORDER: AIProviderName[] = ['anthropic', 'openai', 'google_ai', 'openrouter'];

// ── Constants ──

export const PROVIDER_MODELS: Record<AIProviderName, { id: string; name: string; description?: string }[]> = {
	anthropic: [
		{ id: 'claude-opus-4-20250514', name: 'Claude Opus 4', description: 'Most capable model' },
		{ id: 'claude-sonnet-4-20250514', name: 'Claude Sonnet 4', description: 'Balanced performance' },
		{ id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet', description: 'Fast and capable' },
		{ id: 'claude-3-5-haiku-20241022', name: 'Claude 3.5 Haiku', description: 'Fastest responses' },
	],
	openai: [
		{ id: 'gpt-4.1', name: 'GPT-4.1', description: 'Latest and most capable' },
		{ id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini', description: 'Fast and efficient' },
		{ id: 'gpt-4o', name: 'GPT-4o', description: 'Multimodal model' },
		{ id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Lightweight multimodal' },
		{ id: 'o3', name: 'o3', description: 'Advanced reasoning' },
		{ id: 'o3-mini', name: 'o3 Mini', description: 'Fast reasoning' },
	],
	google_ai: [
		{ id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Most capable reasoning' },
		{ id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Fast and efficient' },
		{ id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: 'Balanced performance' },
		{ id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', description: 'Previous generation' },
	],
	openrouter: [
		{ id: 'anthropic/claude-opus-4', name: 'Claude Opus 4', description: 'Via OpenRouter' },
		{ id: 'anthropic/claude-sonnet-4', name: 'Claude Sonnet 4', description: 'Via OpenRouter' },
		{ id: 'openai/gpt-4.1', name: 'GPT-4.1', description: 'Via OpenRouter' },
		{ id: 'openai/o3', name: 'o3', description: 'Via OpenRouter' },
		{ id: 'google/gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Via OpenRouter' },
		{ id: 'meta-llama/llama-4-maverick', name: 'Llama 4 Maverick', description: 'Via OpenRouter' },
		{ id: 'deepseek/deepseek-r1', name: 'DeepSeek R1', description: 'Via OpenRouter' },
	],
};

const THREAT_ACTION_OPTIONS: { value: ThreatActionValue; label: string; description: string }[] = [
	{ value: 'block', label: 'Block', description: 'Immediately block the message' },
	{ value: 'request', label: 'MFA Challenge', description: 'Ask for TOTP code before allowing' },
	{ value: 'ignore', label: 'Ignore', description: 'Silently drop the message without any response' },
	{ value: 'log', label: 'Log Only', description: 'Log the threat but allow through' },
];

const THREAT_ACTION_SEVERITIES = [
	{ key: 'critical' as const, label: 'Critical', color: 'text-red-600 dark:text-red-400', description: 'Attacks that could compromise your system' },
	{ key: 'high' as const, label: 'High', color: 'text-orange-600 dark:text-orange-400', description: 'Prompt injection and manipulation attempts' },
	{ key: 'medium' as const, label: 'Medium', color: 'text-yellow-600 dark:text-yellow-400', description: 'Suspicious patterns that may be harmful' },
	{ key: 'low' as const, label: 'Low', color: 'text-slate-600 dark:text-slate-400', description: 'Minor anomalies, usually safe' },
];

const DEFAULT_THREAT_ACTIONS: ThreatActionsConfig = {
	critical: 'block',
	high: 'block',
	medium: 'log',
	low: 'log',
};

const PAM_ACTION_OPTIONS: { value: PAMActionValue; label: string; description: string }[] = [
	{ value: 'block', label: 'Block', description: 'Immediately block the message' },
	{ value: 'mfa_challenge', label: 'MFA Challenge', description: 'Ask for TOTP code before allowing' },
	{ value: 'allow', label: 'Allow', description: 'Allow without verification' },
];

type PAMScopeKey = 'destructive' | 'credential' | 'system_admin' | 'code_execution';

const PAM_SCOPES: { key: PAMScopeKey; label: string; description: string; color: string }[] = [
	{ key: 'destructive', label: 'Destructive Commands', description: 'File deletion, disk formatting, data destruction', color: 'text-red-600 dark:text-red-400' },
	{ key: 'credential', label: 'Credential Access', description: 'Reading passwords, tokens, API keys', color: 'text-orange-600 dark:text-orange-400' },
	{ key: 'system_admin', label: 'System Administration', description: 'Installing software, changing system settings', color: 'text-yellow-600 dark:text-yellow-400' },
	{ key: 'code_execution', label: 'Code Execution', description: 'Running scripts, compiling code', color: 'text-blue-600 dark:text-blue-400' },
];

const DEFAULT_PAM_SCOPES: PAMScopesConfig = {
	destructive: { enabled: true, action: 'mfa_challenge' },
	credential: { enabled: true, action: 'mfa_challenge' },
	system_admin: { enabled: true, action: 'mfa_challenge' },
	code_execution: { enabled: false, action: 'allow' },
};

type PAMRiskKey = 'critical' | 'high' | 'medium' | 'low';

const PAM_RISK_LEVELS: { key: PAMRiskKey; label: string; color: string }[] = [
	{ key: 'critical', label: 'Critical', color: 'text-red-600 dark:text-red-400' },
	{ key: 'high', label: 'High', color: 'text-orange-600 dark:text-orange-400' },
	{ key: 'medium', label: 'Medium', color: 'text-yellow-600 dark:text-yellow-400' },
	{ key: 'low', label: 'Low', color: 'text-slate-600 dark:text-slate-400' },
];

const DEFAULT_PAM_RISK_ACTIONS: PAMRiskActionsConfig = {
	critical: 'block',
	high: 'mfa_challenge',
	medium: 'allow',
	low: 'allow',
};

// ── Props ──

export interface PolicyConfigTabsProps {
	config: PolicyConfig;
	onConfigChange: (updates: Partial<PolicyConfig>) => void;
	/** When provided, fields differing from teamDefault show an "overridden" badge */
	teamDefault?: PolicyConfig;
	/** Hide integrations gateway status section (only relevant for instance-level) */
	hideGatewayStatus?: boolean;
	/** When true, show per-section override toggles (instance-level only) */
	showOverrideToggles?: boolean;
	/** Callback for AI provider credential actions — if absent, credential buttons are hidden */
	onConnectProvider?: (provider: AIProviderName) => void;
	onRemoveProvider?: (provider: AIProviderName) => void;
	/** Gateway config editor props (instance-level only) */
	gatewayConfig?: string | null;
	onFetchGatewayConfig?: () => void;
	onSaveGatewayConfig?: (config: string) => void;
	loadingGatewayConfig?: boolean;
	savingGatewayConfig?: boolean;
	gatewayConfigStatus?: { type: 'success' | 'error'; message: string } | null;
	/** Channel key assignments for the current agent (channel_type → label) */
	channelKeyAssignments?: Record<string, string>;
	/** Channel keys for team-level management (shown inline in integration cards) */
	channelKeys?: ChannelKey[];
	/** Callback to add a new channel key for a specific channel type */
	onAddChannelKey?: (channelType: string) => void;
	/** Callback to edit an existing channel key */
	onEditChannelKey?: (key: ChannelKey) => void;
	/** Callback to delete a channel key */
	onDeleteChannelKey?: (keyId: string) => void;
	/** List of team agents for key assignment dropdown */
	teamAgents?: TeamAgent[];
	/** Callback to assign a channel key to a specific instance */
	onAssignChannelKey?: (keyId: string, instanceId: string) => void;
	/** Optional Skills tab content — when provided, a "Skills" tab is shown */
	skillsContent?: React.ReactNode;
	/** Optional Access tab content — when provided, an "Access" tab is shown */
	accessContent?: React.ReactNode;
	/** Optional Init tab content — when provided, an "Init" tab is shown */
	initContent?: React.ReactNode;
}

// ── Helper: deep-compare a specific field against team default ──

function isOverridden(teamDefault: PolicyConfig | undefined, config: PolicyConfig, field: string): boolean {
	if (!teamDefault) return false;
	const a = (teamDefault as any)[field];
	const b = (config as any)[field];
	return JSON.stringify(a) !== JSON.stringify(b);
}

function OverrideBadge({ teamDefault, config, field }: { teamDefault?: PolicyConfig; config: PolicyConfig; field: string }) {
	if (!isOverridden(teamDefault, config, field)) return null;
	return (
		<span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
			overridden
		</span>
	);
}

// ── Helper: resolve whether a section is customized ──
// When section_overrides is undefined (legacy instances), all sections are ON.

type SectionKey = 'security' | 'ai_providers' | 'integrations';

function isSectionCustomized(config: PolicyConfig, section: SectionKey): boolean {
	if (!config.section_overrides) return true; // backward compat: all ON
	return !!config.section_overrides[section];
}

const SECTION_LABELS: Record<SectionKey, string> = {
	security: 'Security',
	ai_providers: 'AI Providers',
	integrations: 'Integrations',
};

function SectionOverrideToggle({
	section,
	config,
	teamDefault,
	onConfigChange,
}: {
	section: SectionKey;
	config: PolicyConfig;
	teamDefault?: PolicyConfig;
	onConfigChange: (updates: Partial<PolicyConfig>) => void;
}) {
	if (!teamDefault) return null;

	const customized = isSectionCustomized(config, section);

	const handleToggle = () => {
		const newOverrides = {
			...(config.section_overrides || { security: true, ai_providers: true, integrations: true }),
			[section]: !customized,
		};

		if (customized) {
			// Turning OFF: copy team default values for this section into config
			const sectionDefaults = getSectionDefaults(section, teamDefault);
			onConfigChange({ ...sectionDefaults, section_overrides: newOverrides });
		} else {
			// Turning ON: just update the toggle, keep current (team default) values as starting point
			onConfigChange({ section_overrides: newOverrides });
		}
	};

	return (
		<div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium text-slate-900 dark:text-white">
						Use custom settings for {SECTION_LABELS[section]}
					</p>
					<p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
						{customized
							? 'This instance uses its own settings for this section.'
							: 'Using team defaults — changes apply to all workspaces'}
					</p>
				</div>
				<button
					onClick={handleToggle}
					className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
						customized ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'
					}`}
				>
					<span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
						customized ? 'translate-x-6' : 'translate-x-1'
					}`} />
				</button>
			</div>
		</div>
	);
}

/** Get team default field values for a given section, to be spread into config when toggling OFF. */
function getSectionDefaults(section: SectionKey, teamDefault: PolicyConfig): Partial<PolicyConfig> {
	switch (section) {
		case 'security':
			return {
				enable_threat_detection: teamDefault.enable_threat_detection,
				threat_actions: teamDefault.threat_actions,
				enable_pam: teamDefault.enable_pam,
				pam_scopes: teamDefault.pam_scopes,
				pam_risk_actions: teamDefault.pam_risk_actions,
				mfa_challenge_timeout_seconds: teamDefault.mfa_challenge_timeout_seconds,
				blocked_patterns: teamDefault.blocked_patterns,
				log_all_requests: teamDefault.log_all_requests,
				log_responses: teamDefault.log_responses,
				alert_on_threats: teamDefault.alert_on_threats,
			};
		case 'ai_providers':
			return {
				ai_providers: teamDefault.ai_providers,
				provider_order: teamDefault.provider_order,
				allowed_models: teamDefault.allowed_models,
				blocked_models: teamDefault.blocked_models,
			};
		case 'integrations':
			return {
				integrations: teamDefault.integrations,
			};
	}
}

// ── Component ──

export default function PolicyConfigTabs({
	config,
	onConfigChange,
	teamDefault,
	hideGatewayStatus,
	showOverrideToggles,
	onConnectProvider,
	onRemoveProvider,
	gatewayConfig,
	onFetchGatewayConfig,
	onSaveGatewayConfig,
	loadingGatewayConfig,
	savingGatewayConfig,
	gatewayConfigStatus,
	channelKeyAssignments,
	channelKeys,
	onAddChannelKey,
	onEditChannelKey,
	onDeleteChannelKey,
	teamAgents,
	onAssignChannelKey,
	skillsContent,
	accessContent,
	initContent,
}: PolicyConfigTabsProps) {
	const [activeTab, setActiveTab] = useState<'security' | 'ai_providers' | 'integrations' | 'gateway_config' | 'skills' | 'access' | 'init'>('security');
	const [editedConfig, setEditedConfig] = useState<string>('');
	const [jsonError, setJsonError] = useState<string | null>(null);

	const updateConfig = (updates: Partial<PolicyConfig>) => {
		onConfigChange(updates);
	};

	const updateIntegration = <K extends keyof IntegrationsConfig>(
		integration: K,
		updates: Partial<IntegrationsConfig[K]>
	) => {
		onConfigChange({
			integrations: {
				...config.integrations,
				[integration]: {
					...config.integrations[integration],
					...updates,
				},
			},
		});
	};

	const toggleIntegration = (integration: keyof IntegrationsConfig) => {
		updateIntegration(integration, { enabled: !config.integrations[integration].enabled } as any);
	};

	// Determine which sections are disabled (using team defaults, read-only)
	const securityDisabled = showOverrideToggles && teamDefault && !isSectionCustomized(config, 'security');
	const aiProvidersDisabled = showOverrideToggles && teamDefault && !isSectionCustomized(config, 'ai_providers');
	const integrationsDisabled = showOverrideToggles && teamDefault && !isSectionCustomized(config, 'integrations');

	return (
		<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
			{/* Tabs */}
			<div className="border-b border-slate-200 dark:border-slate-800">
				<nav className="flex -mb-px overflow-x-auto">
					{[
						{ id: 'security', label: 'Security' },
						{ id: 'ai_providers', label: 'AI Providers' },
						{ id: 'integrations', label: 'Integrations' },
						...(!hideGatewayStatus ? [{ id: 'gateway_config', label: 'Advanced' }] : []),
						...(skillsContent ? [{ id: 'skills', label: 'Skills' }] : []),
						...(accessContent ? [{ id: 'access', label: 'Access' }] : []),
						...(initContent ? [{ id: 'init', label: 'Init' }] : []),
					].map((tab) => (
						<button
							key={tab.id}
							onClick={() => setActiveTab(tab.id as any)}
							className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
								activeTab === tab.id
									? 'border-primary-600 text-primary-600 dark:text-primary-400'
									: 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300'
							}`}
						>
							{tab.label}
						</button>
					))}
				</nav>
			</div>

			<div className="p-6">
				{/* ══════════════════════ Security Tab ══════════════════════ */}
				{activeTab === 'security' && (
					<div className="space-y-8">
						{showOverrideToggles && teamDefault && (
							<SectionOverrideToggle
								section="security"
								config={config}
								teamDefault={teamDefault}
								onConfigChange={onConfigChange}
							/>
						)}
						<div className={securityDisabled ? 'pointer-events-none opacity-60' : ''}>
						{/* Threat Protection */}
						<div>
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
								Threat Protection
								<OverrideBadge teamDefault={teamDefault} config={config} field="enable_threat_detection" />
							</h3>
							<p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Automatically detect and block malicious prompts before they reach your bot</p>
							<div className="space-y-4">
								<label className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
									<div>
										<p className="font-medium text-slate-900 dark:text-white">Enable Threat Protection</p>
										<p className="text-sm text-slate-500 dark:text-slate-400">Scan requests for malicious patterns</p>
									</div>
									<button
										onClick={() => updateConfig({ enable_threat_detection: !config.enable_threat_detection })}
										className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
											config.enable_threat_detection ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'
										}`}
									>
										<span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
											config.enable_threat_detection ? 'translate-x-6' : 'translate-x-1'
										}`} />
									</button>
								</label>
							</div>
						</div>

						{/* Threat Response */}
						{config.enable_threat_detection && (
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
									Threat Response
									<OverrideBadge teamDefault={teamDefault} config={config} field="threat_actions" />
								</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
									Choose what happens when a threat of each severity is detected. "MFA Challenge" requires the user to verify their identity with a TOTP code before the message is allowed through.
								</p>
								<div className="space-y-3">
									{THREAT_ACTION_SEVERITIES.map((sev) => {
										const actions = config.threat_actions ?? DEFAULT_THREAT_ACTIONS;
										const current = actions[sev.key] ?? DEFAULT_THREAT_ACTIONS[sev.key];
										return (
											<div key={sev.key} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
												<p className={`text-sm font-semibold ${sev.color}`}>{sev.label} Severity</p>
												<p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{sev.description}</p>
												<div className="grid grid-cols-4 gap-2">
													{THREAT_ACTION_OPTIONS.map((opt) => (
														<button
															key={opt.value}
															onClick={() => {
																const prev = config.threat_actions ?? { ...DEFAULT_THREAT_ACTIONS };
																updateConfig({ threat_actions: { ...prev, [sev.key]: opt.value } });
															}}
															className={`p-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
																current === opt.value
																	? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-2 border-primary-500'
																	: 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-2 border-transparent hover:border-slate-300 dark:hover:border-slate-500'
															}`}
															title={opt.description}
														>
															{opt.label}
														</button>
													))}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Access Control */}
						<div>
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
								Access Control
								<OverrideBadge teamDefault={teamDefault} config={config} field="enable_pam" />
							</h3>
							<p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Control what system actions your bot is allowed to perform</p>
							<div className="space-y-4">
								<label className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
									<div>
										<p className="font-medium text-slate-900 dark:text-white">Enable Access Control</p>
										<p className="text-sm text-slate-500 dark:text-slate-400">Control what system actions your bot is allowed to perform</p>
									</div>
									<button
										onClick={() => updateConfig({ enable_pam: !config.enable_pam })}
										className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
											config.enable_pam ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'
										}`}
									>
										<span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
											config.enable_pam ? 'translate-x-6' : 'translate-x-1'
										}`} />
									</button>
								</label>
							</div>
						</div>

						{/* Permission Levels */}
						{config.enable_pam && (
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
									Permission Levels
									<OverrideBadge teamDefault={teamDefault} config={config} field="pam_scopes" />
								</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
									Choose what happens when a privileged operation of each type is detected. "MFA Challenge" requires the user to verify with a TOTP code before the operation is allowed.
								</p>
								<div className="space-y-3">
									{PAM_SCOPES.map((scope) => {
										const scopes = config.pam_scopes ?? DEFAULT_PAM_SCOPES;
										const scopeConfig = scopes[scope.key] ?? DEFAULT_PAM_SCOPES[scope.key];
										return (
											<div key={scope.key} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
												<div className="flex items-center justify-between mb-2">
													<div>
														<p className={`text-sm font-semibold ${scope.color}`}>{scope.label}</p>
														<p className="text-xs text-slate-500 dark:text-slate-400">{scope.description}</p>
													</div>
													<button
														onClick={() => {
															const prev = config.pam_scopes ?? { ...DEFAULT_PAM_SCOPES };
															const prevScope = prev[scope.key] ?? DEFAULT_PAM_SCOPES[scope.key];
															updateConfig({
																pam_scopes: { ...prev, [scope.key]: { ...prevScope, enabled: !prevScope.enabled } },
															});
														}}
														className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
															scopeConfig.enabled ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'
														}`}
													>
														<span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
															scopeConfig.enabled ? 'translate-x-5' : 'translate-x-1'
														}`} />
													</button>
												</div>
												{scopeConfig.enabled && (
													<div className="grid grid-cols-3 gap-2 mt-2">
														{PAM_ACTION_OPTIONS.map((opt) => (
															<button
																key={opt.value}
																onClick={() => {
																	const prev = config.pam_scopes ?? { ...DEFAULT_PAM_SCOPES };
																	const prevScope = prev[scope.key] ?? DEFAULT_PAM_SCOPES[scope.key];
																	updateConfig({
																		pam_scopes: { ...prev, [scope.key]: { ...prevScope, action: opt.value } },
																	});
																}}
																className={`p-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
																	scopeConfig.action === opt.value
																		? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-2 border-primary-500'
																		: 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-2 border-transparent hover:border-slate-300 dark:hover:border-slate-500'
																}`}
																title={opt.description}
															>
																{opt.label}
															</button>
														))}
													</div>
												)}
											</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Risk Level Actions */}
						{config.enable_pam && (
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
									Risk Level Actions
									<OverrideBadge teamDefault={teamDefault} config={config} field="pam_risk_actions" />
								</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
									Set actions based on command risk level. The most restrictive action between scope-based and risk-based rules applies.
								</p>
								<div className="space-y-3">
									{PAM_RISK_LEVELS.map((level) => {
										const riskActions = config.pam_risk_actions ?? DEFAULT_PAM_RISK_ACTIONS;
										const currentAction = riskActions[level.key] ?? DEFAULT_PAM_RISK_ACTIONS[level.key];
										return (
											<div key={level.key} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
												<p className={`text-sm font-semibold mb-2 ${level.color}`}>{level.label} Risk</p>
												<div className="grid grid-cols-3 gap-2">
													{PAM_ACTION_OPTIONS.map((opt) => (
														<button
															key={opt.value}
															onClick={() => {
																const prev = config.pam_risk_actions ?? { ...DEFAULT_PAM_RISK_ACTIONS };
																updateConfig({ pam_risk_actions: { ...prev, [level.key]: opt.value } });
															}}
															className={`p-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
																currentAction === opt.value
																	? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-2 border-primary-500'
																	: 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-2 border-transparent hover:border-slate-300 dark:hover:border-slate-500'
															}`}
															title={opt.description}
														>
															{opt.label}
														</button>
													))}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}

						{/* MFA Challenge Timeout */}
						{config.enable_pam && (
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
									MFA Challenge Timeout
									<OverrideBadge teamDefault={teamDefault} config={config} field="mfa_challenge_timeout_seconds" />
								</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
									How long a user has to respond to an MFA challenge before it expires and the operation is blocked.
								</p>
								<div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
									<div className="flex items-center gap-4">
										<input
											type="range"
											min={10}
											max={300}
											step={5}
											value={config.mfa_challenge_timeout_seconds ?? 120}
											onChange={(e) => updateConfig({ mfa_challenge_timeout_seconds: parseInt(e.target.value) })}
											className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-primary-600"
										/>
										<span className="text-sm font-mono font-medium text-slate-900 dark:text-white w-16 text-right">
											{config.mfa_challenge_timeout_seconds ?? 120}s
										</span>
									</div>
								</div>
							</div>
						)}
						</div>{/* end security disabled wrapper */}
					</div>
				)}


				{/* ══════════════════════ AI Providers Tab ══════════════════════ */}
				{activeTab === 'ai_providers' && (() => {
					const providerOrder = config.provider_order || DEFAULT_PROVIDER_ORDER;

					const moveProvider = (index: number, direction: 'up' | 'down') => {
						const newOrder = [...providerOrder];
						const swapIndex = direction === 'up' ? index - 1 : index + 1;
						[newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
						onConfigChange({ provider_order: newOrder });
					};

					const providerInfoMap: Record<AIProviderName, { name: string; icon: React.ReactNode; iconBg: string }> = {
						anthropic: {
							name: 'Anthropic',
							icon: <span className="text-white font-bold text-lg">A</span>,
							iconBg: 'bg-gradient-to-br from-orange-500 to-orange-600',
						},
						openai: {
							name: 'OpenAI',
							icon: <svg className="w-6 h-6 text-white dark:text-slate-900" viewBox="0 0 24 24" fill="currentColor"><path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/></svg>,
							iconBg: 'bg-slate-900 dark:bg-white',
						},
						google_ai: {
							name: 'Google AI',
							icon: <svg className="w-6 h-6" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>,
							iconBg: 'bg-white border border-slate-200',
						},
						openrouter: {
							name: 'OpenRouter',
							icon: <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
							iconBg: 'bg-violet-600',
						},
					};

					return (
					<div className="space-y-6">
						{showOverrideToggles && teamDefault && (
							<SectionOverrideToggle
								section="ai_providers"
								config={config}
								teamDefault={teamDefault}
								onConfigChange={onConfigChange}
							/>
						)}
						<div className={aiProvidersDisabled ? 'pointer-events-none opacity-60 space-y-6' : 'space-y-6'}>
						<div>
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white">AI Providers</h3>
							<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
								Configure API keys and select allowed models for each provider. Providers are tried in order from top to bottom. Use arrows to reorder.
							</p>
						</div>

						{providerOrder.map((providerKey, index) => {
							const provider = config.ai_providers?.[providerKey];
							const models = PROVIDER_MODELS[providerKey];
							const providerInfo = providerInfoMap[providerKey];
							const isFirst = index === 0;
							const isLast = index === providerOrder.length - 1;

							return (
								<div key={providerKey} className={`border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden${provider?.is_configured && provider?.enabled === false ? " opacity-60" : ""}`}>
									<div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800">
										<div className="flex items-center space-x-3">
											{/* Position badge and reorder arrows */}
											<div className="flex items-center space-x-1.5">
												<span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300">
													#{index + 1}
												</span>
												<div className="flex flex-col">
													<button
														onClick={() => moveProvider(index, 'up')}
														disabled={isFirst}
														className={`p-0.5 rounded transition-colors ${
															isFirst
																? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
																: 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 cursor-pointer'
														}`}
														title="Move up"
													>
														<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
														</svg>
													</button>
													<button
														onClick={() => moveProvider(index, 'down')}
														disabled={isLast}
														className={`p-0.5 rounded transition-colors ${
															isLast
																? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
																: 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 cursor-pointer'
														}`}
														title="Move down"
													>
														<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
														</svg>
													</button>
												</div>
											</div>
											<div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${providerInfo.iconBg}`}>
												{providerInfo.icon}
											</div>
											<div>
												<p className="font-medium text-slate-900 dark:text-white">{providerInfo.name}</p>
												<p className="text-sm text-slate-500 dark:text-slate-400">
													{provider?.is_configured ? (
														provider?.enabled !== false ? (
															<span className="text-green-600 dark:text-green-400">API key configured</span>
														) : (
															<span className="text-amber-600 dark:text-amber-400">Disabled</span>
														)
													) : (
														'Not configured'
													)}
												</p>
											</div>
										</div>
										<div className="flex items-center space-x-3">
											{provider?.is_configured && (
												<button
													onClick={() => {
														if (!config.ai_providers) return;
														onConfigChange({
															ai_providers: {
																...config.ai_providers,
																[providerKey]: {
																	...config.ai_providers[providerKey],
																	enabled: !provider.enabled,
																},
															},
														});
													}}
													className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
														provider.enabled !== false ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'
													}`}
													title={provider.enabled !== false ? 'Disable provider' : 'Enable provider'}
												>
													<span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
														provider.enabled !== false ? 'translate-x-6' : 'translate-x-1'
													}`} />
												</button>
											)}
											{provider?.is_configured && onRemoveProvider && (
												<button
													onClick={() => onRemoveProvider(providerKey)}
													className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors cursor-pointer"
												>
													Remove
												</button>
											)}
											{onConnectProvider && (
												<button
													onClick={() => onConnectProvider(providerKey)}
													className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
														providerKey === 'anthropic'
															? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
															: 'bg-primary-600 text-white hover:bg-primary-700'
													}`}
												>
													{provider?.is_configured ? (providerKey === 'anthropic' ? 'Update Token' : 'Update Key') : (providerKey === 'anthropic' ? 'Connect Claude' : 'Add Key')}
												</button>
											)}
										</div>
									</div>

									{/* Model Selection */}
									{provider?.is_configured && (
										<div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
											<p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
												Allowed Models
												<span className="text-slate-400 dark:text-slate-500 font-normal ml-2">
													({provider.allowed_models?.length || 0} selected)
												</span>
											</p>
											<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
												{models.map((model) => {
													const isSelected = provider.allowed_models?.includes(model.id);
													return (
														<label
															key={model.id}
															className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
																isSelected
																	? 'bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500'
																	: 'bg-slate-50 dark:bg-slate-800 border-2 border-transparent hover:border-slate-300 dark:hover:border-slate-600'
															}`}
														>
															<input
																type="checkbox"
																checked={isSelected}
																onChange={() => {
																	if (!config.ai_providers) return;
																	const currentModels = provider.allowed_models || [];
																	const newModels = isSelected
																		? currentModels.filter(m => m !== model.id)
																		: [...currentModels, model.id];
																	onConfigChange({
																		ai_providers: {
																			...config.ai_providers,
																			[providerKey]: {
																				...config.ai_providers[providerKey],
																				allowed_models: newModels,
																			},
																		},
																	});
																}}
																className="sr-only"
															/>
															<div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 ${
																isSelected ? 'bg-primary-500 border-primary-500' : 'border-slate-300 dark:border-slate-600'
															}`}>
																{isSelected && (
																	<svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
																	</svg>
																)}
															</div>
															<div className="min-w-0">
																<p className="font-medium text-slate-900 dark:text-white text-sm">{model.name}</p>
																{model.description && (
																	<p className="text-xs text-slate-500 dark:text-slate-400 truncate">{model.description}</p>
																)}
															</div>
														</label>
													);
												})}
											</div>
										</div>
									)}
								</div>
							);
						})}

						{/* Info section */}
						<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
							<div className="flex items-start space-x-3">
								<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								<div>
									<p className="text-sm font-medium text-blue-800 dark:text-blue-200">How it works</p>
									<ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-disc list-inside">
										<li><strong>Anthropic/Claude:</strong> Run <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">claude setup-token</code> in your terminal to get a token, then paste it here</li>
										<li><strong>Other providers:</strong> Enter your API key manually</li>
										<li><strong>Fallback order:</strong> If the primary provider fails, the next configured provider will be tried automatically</li>
										<li>All credentials are encrypted (AES-256-GCM) and stored securely</li>
										<li>Credentials are automatically synced to your Clawdbot instance</li>
										<li>Only selected models will be available for use</li>
									</ul>
								</div>
							</div>
						</div>
					</div>{/* end ai_providers disabled wrapper */}
					</div>
					);
				})()}

				{/* ══════════════════════ Integrations Tab ══════════════════════ */}
				{activeTab === 'integrations' && (
					<div className="space-y-8">
						{showOverrideToggles && teamDefault && (
							<SectionOverrideToggle
								section="integrations"
								config={config}
								teamDefault={teamDefault}
								onConfigChange={onConfigChange}
							/>
						)}
						<div className={integrationsDisabled ? 'pointer-events-none opacity-60 space-y-8' : 'space-y-8'}>
						<p className="text-sm text-slate-600 dark:text-slate-400">
							Configure bot integrations. <strong>Protected</strong> channels route messages through ClawdGate's threat detection pipeline. <strong>Unmanaged</strong> channels connect directly to OpenClaw and bypass all scanning.
						</p>

						<div>
							<div className="flex items-center space-x-2 mb-1">
								<svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
								</svg>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">ClawdGate Protected Integrations</h3>
							</div>
							<p className="text-sm text-slate-500 dark:text-slate-400 mb-4 ml-7">
								Messages are scanned by ClawdGate's threat detection pipeline (prompt injection, credential leaks) before reaching the LLM.
							</p>

							<div className="space-y-4">
								{/* Telegram */}
								<IntegrationCard
									name="Telegram Bot" description="Connect via Telegram messenger"
									iconBg="bg-blue-500"
									icon={<svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>}
									enabled={config.integrations.telegram.enabled}
									onToggle={() => toggleIntegration('telegram')}
									borderColor="border-teal-200 dark:border-teal-800/50"
									headerBg="bg-teal-50/50 dark:bg-teal-900/10"
								>
									<div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
										<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-medium text-blue-800 dark:text-blue-200">How to set up Telegram</p>
											<ol className="text-sm text-blue-700 dark:text-blue-300 mt-1 list-decimal list-inside space-y-1">
												<li>Open Telegram and search for @BotFather</li>
												<li>Send /newbot and follow the prompts to create a bot</li>
												<li>Copy the Bot Token provided by BotFather</li>
												<li>Optionally restrict access using Allowed Chat IDs below</li>
											</ol>
										</div>
									</div>
									{channelKeys && onAddChannelKey && onEditChannelKey && onDeleteChannelKey ? (
										<ChannelKeyList channelType="telegram" channelKeys={channelKeys} onAdd={onAddChannelKey} onEdit={onEditChannelKey} onDelete={onDeleteChannelKey} teamAgents={teamAgents} onAssign={onAssignChannelKey} />
									) : (
										<>
											{channelKeyAssignments?.telegram && (
												<TeamKeyBanner label={channelKeyAssignments.telegram} />
											)}
											{!channelKeyAssignments?.telegram && (
												<div>
													<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bot Token</label>
													<input type="password" value={config.integrations.telegram.bot_token}
														onChange={(e) => updateIntegration('telegram', { bot_token: e.target.value })}
														placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
														className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
													<p className="text-xs text-slate-500 mt-1">Get this from @BotFather on Telegram</p>
												</div>
											)}
										</>
									)}
									<div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
										<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-medium text-blue-800 dark:text-blue-200">Gateway adapter will be replaced</p>
											<p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">
												Enabling managed Telegram will disable any existing direct Telegram adapter configured in the OpenClaw gateway. All Telegram messages will be routed through ClawdGate for threat scanning instead.
											</p>
										</div>
									</div>
									<div>
										<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Allowed Chat IDs</label>
										<input type="text" value={config.integrations.telegram.allowed_chat_ids.join(', ')}
											onChange={(e) => updateIntegration('telegram', { allowed_chat_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
											placeholder="-1001234567890, 987654321"
											className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
										<p className="text-xs text-slate-500 mt-1">Comma-separated list of allowed chat IDs (leave empty to allow all)</p>
									</div>
								</IntegrationCard>

								{/* Slack */}
								<IntegrationCard
									name="Slack" description="Connect via Slack workspace"
									iconBg="bg-purple-600"
									icon={<svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/></svg>}
									enabled={config.integrations.slack.enabled}
									onToggle={() => toggleIntegration('slack')}
									borderColor="border-teal-200 dark:border-teal-800/50"
									headerBg="bg-teal-50/50 dark:bg-teal-900/10"
								>
									<div className="flex items-start space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
										<svg className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-medium text-purple-800 dark:text-purple-200">How to set up Slack</p>
											<ol className="text-sm text-purple-700 dark:text-purple-300 mt-1 list-decimal list-inside space-y-1">
												<li>Go to api.slack.com/apps and create a new app</li>
												<li>Under OAuth &amp; Permissions, add bot scopes (chat:write, app_mentions:read)</li>
												<li>Install the app to your workspace and copy the Bot Token (xoxb-...)</li>
												<li>Under Basic Information, copy the Signing Secret</li>
												<li>Optionally generate an App-Level Token (xapp-...) for Socket Mode</li>
											</ol>
										</div>
									</div>
									{channelKeys && onAddChannelKey && onEditChannelKey && onDeleteChannelKey ? (
										<ChannelKeyList channelType="slack" channelKeys={channelKeys} onAdd={onAddChannelKey} onEdit={onEditChannelKey} onDelete={onDeleteChannelKey} teamAgents={teamAgents} onAssign={onAssignChannelKey} />
									) : (
										<>
											{channelKeyAssignments?.slack && (
												<TeamKeyBanner label={channelKeyAssignments.slack} />
											)}
											{!channelKeyAssignments?.slack && (
												<>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bot Token</label>
														<input type="password" value={config.integrations.slack.bot_token} onChange={(e) => updateIntegration('slack', { bot_token: e.target.value })} placeholder="xoxb-..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">App Token</label>
														<input type="password" value={config.integrations.slack.app_token} onChange={(e) => updateIntegration('slack', { app_token: e.target.value })} placeholder="xapp-..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Signing Secret</label>
														<input type="password" value={config.integrations.slack.signing_secret} onChange={(e) => updateIntegration('slack', { signing_secret: e.target.value })} placeholder="Enter signing secret" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
													</div>
												</>
											)}
										</>
									)}
									<div>
										<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Allowed Channels</label>
										<input type="text" value={config.integrations.slack.allowed_channels.join(', ')} onChange={(e) => updateIntegration('slack', { allowed_channels: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="#general, #engineering" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
									</div>
								</IntegrationCard>

								{/* Discord */}
								<IntegrationCard
									name="Discord" description="Connect via Discord server"
									iconBg="bg-indigo-600"
									icon={<svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>}
									enabled={config.integrations.discord.enabled}
									onToggle={() => toggleIntegration('discord')}
									borderColor="border-teal-200 dark:border-teal-800/50"
									headerBg="bg-teal-50/50 dark:bg-teal-900/10"
								>
									<div className="flex items-start space-x-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
										<svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-medium text-indigo-800 dark:text-indigo-200">How to set up Discord</p>
											<ol className="text-sm text-indigo-700 dark:text-indigo-300 mt-1 list-decimal list-inside space-y-1">
												<li>Go to discord.com/developers/applications and create a new application</li>
												<li>Under Bot, click "Add Bot" and copy the Bot Token</li>
												<li>Under OAuth2 &gt; URL Generator, select "bot" scope and required permissions</li>
												<li>Use the generated URL to invite the bot to your server</li>
												<li>Optionally restrict by Server IDs and Channel IDs below</li>
											</ol>
										</div>
									</div>
									{channelKeys && onAddChannelKey && onEditChannelKey && onDeleteChannelKey ? (
										<ChannelKeyList channelType="discord" channelKeys={channelKeys} onAdd={onAddChannelKey} onEdit={onEditChannelKey} onDelete={onDeleteChannelKey} teamAgents={teamAgents} onAssign={onAssignChannelKey} />
									) : (
										<>
											{channelKeyAssignments?.discord && (
												<TeamKeyBanner label={channelKeyAssignments.discord} />
											)}
											{!channelKeyAssignments?.discord && (
												<div>
													<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Bot Token</label>
													<input type="password" value={config.integrations.discord.bot_token} onChange={(e) => updateIntegration('discord', { bot_token: e.target.value })} placeholder="Enter bot token" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
												</div>
											)}
										</>
									)}
									<div>
										<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Allowed Server IDs</label>
										<input type="text" value={config.integrations.discord.allowed_guild_ids.join(', ')} onChange={(e) => updateIntegration('discord', { allowed_guild_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="123456789012345678" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
									</div>
									<div>
										<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Allowed Channel IDs</label>
										<input type="text" value={config.integrations.discord.allowed_channel_ids.join(', ')} onChange={(e) => updateIntegration('discord', { allowed_channel_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="123456789012345678" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
									</div>
								</IntegrationCard>

								{/* WeChat */}
								<IntegrationCard
									icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
									iconBg="bg-green-500"
									name="WeChat"
									description="Connect via WeChat Official Account"
									enabled={config.integrations.wechat?.enabled ?? false}
									onToggle={() => toggleIntegration('wechat')}
									borderColor="border-teal-200 dark:border-teal-800/50"
									headerBg="bg-teal-50/50 dark:bg-teal-900/10"
								>
									<div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
										<svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-medium text-green-800 dark:text-green-200">How to set up WeChat</p>
											<ol className="text-sm text-green-700 dark:text-green-300 mt-1 list-decimal list-inside space-y-1">
												<li>Register a WeChat Official Account at <span className="font-mono text-xs">mp.weixin.qq.com</span></li>
												<li>Go to <b>Settings &amp; Development &gt; Basic Configuration</b></li>
												<li>Copy the <b>AppID</b> and <b>AppSecret</b> from the Developer ID section</li>
												<li>Under Server Configuration, set a custom <b>Token</b> and optionally an <b>EncodingAESKey</b></li>
												<li>Set the Server URL (webhook) to your ClawdGate adapter endpoint</li>
												<li>Enable the server configuration and paste the credentials below</li>
											</ol>
										</div>
									</div>
									{channelKeys && onAddChannelKey && onEditChannelKey && onDeleteChannelKey ? (
										<ChannelKeyList channelType="wechat" channelKeys={channelKeys} onAdd={onAddChannelKey} onEdit={onEditChannelKey} onDelete={onDeleteChannelKey} teamAgents={teamAgents} onAssign={onAssignChannelKey} />
									) : (
										<>
											{channelKeyAssignments?.wechat && (
												<TeamKeyBanner label={channelKeyAssignments.wechat} />
											)}
											{!channelKeyAssignments?.wechat && (
												<>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">App ID</label>
														<input type="text" value={config.integrations.wechat?.app_id || ''} onChange={(e) => updateIntegration('wechat', { app_id: e.target.value })} placeholder="wx1234567890abcdef" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Found in Basic Configuration &gt; Developer ID (AppID)</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">App Secret</label>
														<input type="password" value={config.integrations.wechat?.app_secret || ''} onChange={(e) => updateIntegration('wechat', { app_secret: e.target.value })} placeholder="Enter app secret" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Click "Reset" next to AppSecret to generate a new one</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Token</label>
														<input type="password" value={config.integrations.wechat?.token || ''} onChange={(e) => updateIntegration('wechat', { token: e.target.value })} placeholder="Verification token" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Custom token you set in Server Configuration for webhook verification</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">EncodingAESKey <span className="text-slate-400">(optional)</span></label>
														<input type="password" value={config.integrations.wechat?.encoding_aes_key || ''} onChange={(e) => updateIntegration('wechat', { encoding_aes_key: e.target.value })} placeholder="Message encryption key" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">43-character key for message encryption. Click "Random Generate" in Server Configuration.</p>
													</div>
												</>
											)}
										</>
									)}
								</IntegrationCard>

								{/* WhatsApp */}
								<IntegrationCard
									icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
									iconBg="bg-green-600"
									name="WhatsApp"
									description="Connect via WhatsApp Business API"
									enabled={config.integrations.whatsapp?.enabled ?? false}
									onToggle={() => toggleIntegration('whatsapp')}
									borderColor="border-green-200 dark:border-green-800/50"
									headerBg="bg-green-50/50 dark:bg-green-900/10"
								>
									<div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
										<svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-medium text-green-800 dark:text-green-200">How to set up WhatsApp</p>
											<ol className="text-sm text-green-700 dark:text-green-300 mt-1 list-decimal list-inside space-y-1">
												<li>Go to <span className="font-mono text-xs">developers.facebook.com</span> and create a Meta app</li>
												<li>Add the <b>WhatsApp</b> product to your app</li>
												<li>Under <b>WhatsApp &gt; API Setup</b>, copy the <b>Phone Number ID</b> and <b>Access Token</b></li>
												<li>Note your <b>Business Account ID</b> from Business Settings</li>
												<li>Set a <b>Verify Token</b> for webhook verification (optional)</li>
											</ol>
										</div>
									</div>
									{channelKeys && onAddChannelKey && onEditChannelKey && onDeleteChannelKey ? (
										<ChannelKeyList channelType="whatsapp" channelKeys={channelKeys} onAdd={onAddChannelKey} onEdit={onEditChannelKey} onDelete={onDeleteChannelKey} teamAgents={teamAgents} onAssign={onAssignChannelKey} />
									) : (
										<>
											{channelKeyAssignments?.whatsapp && (
												<TeamKeyBanner label={channelKeyAssignments.whatsapp} />
											)}
											{!channelKeyAssignments?.whatsapp && (
												<>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone Number ID</label>
														<input type="text" value={config.integrations.whatsapp?.phone_number_id || ''} onChange={(e) => updateIntegration('whatsapp', { phone_number_id: e.target.value })} placeholder="1234567890..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Found under WhatsApp &gt; API Setup in your Meta app dashboard</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Access Token</label>
														<input type="password" value={config.integrations.whatsapp?.access_token || ''} onChange={(e) => updateIntegration('whatsapp', { access_token: e.target.value })} placeholder="Access token..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Temporary or permanent access token from the API Setup page</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Business Account ID</label>
														<input type="text" value={config.integrations.whatsapp?.business_account_id || ''} onChange={(e) => updateIntegration('whatsapp', { business_account_id: e.target.value })} placeholder="Business account ID..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Found in Meta Business Suite under Business Settings</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Verify Token <span className="text-slate-400">(optional)</span></label>
														<input type="password" value={config.integrations.whatsapp?.verify_token || ''} onChange={(e) => updateIntegration('whatsapp', { verify_token: e.target.value })} placeholder="Webhook verify token (optional)" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Custom string used to verify webhook subscriptions</p>
													</div>
												</>
											)}
										</>
									)}
								</IntegrationCard>

								{/* iMessage */}
								<IntegrationCard
									icon={<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>}
									iconBg="bg-blue-400"
									name="iMessage"
									description="Connect via Apple Messages for Business"
									enabled={config.integrations.imessage?.enabled ?? false}
									onToggle={() => toggleIntegration('imessage')}
									borderColor="border-blue-200 dark:border-blue-800/50"
									headerBg="bg-blue-50/50 dark:bg-blue-900/10"
								>
									<div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
										<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div>
											<p className="text-sm font-medium text-blue-800 dark:text-blue-200">How to set up iMessage</p>
											<ol className="text-sm text-blue-700 dark:text-blue-300 mt-1 list-decimal list-inside space-y-1">
												<li>Enroll in Apple Business Register at <span className="font-mono text-xs">register.apple.com</span></li>
												<li>Set up <b>Messages for Business</b> and get your <b>Business ID</b></li>
												<li>Configure a <b>Messaging Service Provider (MSP)</b> and note the <b>MSP ID</b></li>
												<li>Generate an <b>API key</b> from the MSP dashboard</li>
											</ol>
										</div>
									</div>
									{channelKeys && onAddChannelKey && onEditChannelKey && onDeleteChannelKey ? (
										<ChannelKeyList channelType="imessage" channelKeys={channelKeys} onAdd={onAddChannelKey} onEdit={onEditChannelKey} onDelete={onDeleteChannelKey} teamAgents={teamAgents} onAssign={onAssignChannelKey} />
									) : (
										<>
											{channelKeyAssignments?.imessage && (
												<TeamKeyBanner label={channelKeyAssignments.imessage} />
											)}
											{!channelKeyAssignments?.imessage && (
												<>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Business ID</label>
														<input type="text" value={config.integrations.imessage?.business_id || ''} onChange={(e) => updateIntegration('imessage', { business_id: e.target.value })} placeholder="Business ID..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Found in your Apple Business Register account</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">MSP ID</label>
														<input type="text" value={config.integrations.imessage?.msp_id || ''} onChange={(e) => updateIntegration('imessage', { msp_id: e.target.value })} placeholder="Messaging Service Provider ID..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Your Messaging Service Provider identifier</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">API Key</label>
														<input type="password" value={config.integrations.imessage?.api_key || ''} onChange={(e) => updateIntegration('imessage', { api_key: e.target.value })} placeholder="API key..." className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400" />
														<p className="text-xs text-slate-500 mt-1">Generated from your MSP dashboard</p>
													</div>
												</>
											)}
										</>
									)}
								</IntegrationCard>

								{/* Webhook (coming soon) */}
								<div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden opacity-60">
									<div className="flex items-center justify-between p-4 bg-slate-50/50 dark:bg-slate-800/30">
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
												<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
											</div>
											<div>
												<p className="font-medium text-slate-900 dark:text-white">Webhook <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">(coming soon)</span></p>
												<p className="text-sm text-slate-500 dark:text-slate-400">Receive requests via HTTP webhook</p>
											</div>
										</div>
										<div className="relative inline-flex h-6 w-11 items-center rounded-full bg-slate-300 dark:bg-slate-600 cursor-not-allowed">
											<span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
										</div>
									</div>
								</div>

								{/* Email (coming soon) */}
								<div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden opacity-60">
									<div className="flex items-center justify-between p-4 bg-slate-50/50 dark:bg-slate-800/30">
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
												<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
											</div>
											<div>
												<p className="font-medium text-slate-900 dark:text-white">Email <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">(coming soon)</span></p>
												<p className="text-sm text-slate-500 dark:text-slate-400">Interact via email messages</p>
											</div>
										</div>
										<div className="relative inline-flex h-6 w-11 items-center rounded-full bg-slate-300 dark:bg-slate-600 cursor-not-allowed">
											<span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Unmanaged channels section - only shown for instance config, not team defaults */}
						{!hideGatewayStatus && (
							<div>
								<div className="flex items-center space-x-2 mb-1">
									<svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
									</svg>
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white">OpenClaw Direct Channels — Unmanaged</h3>
								</div>
								<p className="text-sm text-slate-500 dark:text-slate-400 mb-1 ml-7">
									Channels configured natively in OpenClaw. These connect directly to the gateway without ClawdGate intermediation.
								</p>
								<p className="text-xs text-slate-400 dark:text-slate-500 mb-4 ml-7 font-mono">
									Config file: ~/.openclaw/openclaw.json
								</p>
								<div className="mb-4 flex items-start space-x-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl">
									<svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
									</svg>
									<div>
										<p className="text-sm font-medium text-amber-800 dark:text-amber-200">Security Notice</p>
										<p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
											Channels listed here bypass ClawdGate threat detection. Messages are <strong>not</strong> scanned for prompt injection, credential leaks, or other threats. For production use, configure channels through <strong>Protected Integrations</strong> above.
										</p>
									</div>
								</div>
							</div>
						)}
						</div>{/* end integrations disabled wrapper */}
					</div>
				)}

				{/* ══════════════════════ Advanced Tab ══════════════════════ */}
				{activeTab === 'gateway_config' && (
					<div className="space-y-6">
						<div>
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Advanced Configuration</h3>
							<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
								Raw OpenClaw gateway configuration for this instance. Each instance has its own isolated config file. Channels managed by ClawdGate should <strong>not</strong> be enabled here to avoid conflicts.
							</p>
						</div>

						{/* Status message */}
						{gatewayConfigStatus && (
							<div className={`rounded-lg p-3 flex items-center space-x-2 ${
								gatewayConfigStatus.type === 'success'
									? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
									: 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
							}`}>
								<svg className={`w-4 h-4 flex-shrink-0 ${
									gatewayConfigStatus.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
								}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
									{gatewayConfigStatus.type === 'success' ? (
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									) : (
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
									)}
								</svg>
								<span className={`text-sm ${
									gatewayConfigStatus.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
								}`}>
									{gatewayConfigStatus.message}
								</span>
							</div>
						)}

						{/* Fetch button */}
						{!gatewayConfig && (
							<button
								onClick={onFetchGatewayConfig}
								disabled={loadingGatewayConfig}
								className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 cursor-pointer"
							>
								{loadingGatewayConfig ? (
									<>
										<svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
										</svg>
										<span>Fetching...</span>
									</>
								) : (
									<>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
										</svg>
										<span>Fetch Config</span>
									</>
								)}
							</button>
						)}

						{/* Config editor */}
						{gatewayConfig !== undefined && gatewayConfig !== null && (() => {
							// Parse config for channel conflict detection
							const currentText = editedConfig || gatewayConfig;
							let parsedGwConfig: any = null;
							try {
								parsedGwConfig = JSON.parse(currentText);
							} catch {
								// Invalid JSON — conflict checks skipped
							}

							// Detect channel conflicts
							const conflicts: { channel: string; message: string }[] = [];
							if (parsedGwConfig) {
								const gwChannels = parsedGwConfig.channels || {};
								const gwPlugins = parsedGwConfig.plugins?.entries || {};

								const channelNames: Record<string, string> = {
									telegram: 'Telegram',
									slack: 'Slack',
									discord: 'Discord',
								};

								for (const [key, displayName] of Object.entries(channelNames)) {
									const gwEnabled = gwChannels[key]?.enabled === true || gwPlugins[key]?.enabled === true;
									const cgEnabled = (config.integrations as any)?.[key]?.enabled === true;

									if (gwEnabled && cgEnabled) {
										conflicts.push({
											channel: displayName,
											message: `${displayName} is enabled in both the OpenClaw gateway config and ClawdGate integrations. This will cause conflicts (e.g. Telegram 409 errors). Disable it here or remove it from ClawdGate integrations.`,
										});
									}
								}

								// Special email warning
								if (gwChannels.email?.enabled === true) {
									const hasEmailConflict = conflicts.some(c => c.channel === 'Email');
									if (!hasEmailConflict) {
										conflicts.push({
											channel: 'Email',
											message: 'Email channel is enabled in the gateway config. Email channels should be carefully managed to avoid duplicate message processing.',
										});
									}
								}
							}

							return (
								<div className="space-y-4">
									{/* Channel conflict warnings */}
									{conflicts.map((conflict, idx) => (
										<div key={idx} className="flex items-start space-x-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
											<svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
											</svg>
											<div>
												<p className="text-sm font-medium text-red-800 dark:text-red-200">Channel Conflict: {conflict.channel}</p>
												<p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{conflict.message}</p>
											</div>
										</div>
									))}

									{/* JSON editor */}
									<textarea
										value={editedConfig || gatewayConfig}
										onChange={(e) => {
											setEditedConfig(e.target.value);
											try {
												JSON.parse(e.target.value);
												setJsonError(null);
											} catch (err: any) {
												setJsonError(err.message);
											}
										}}
										className="w-full h-96 px-4 py-3 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-y focus:outline-none focus:border-primary-500"
										spellCheck={false}
									/>

									{/* JSON validation error */}
									{jsonError && (
										<div className="flex items-center space-x-2 text-sm text-red-600 dark:text-red-400">
											<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
											<span>Invalid JSON: {jsonError}</span>
										</div>
									)}

									{/* Action buttons */}
									<div className="flex items-center space-x-3">
										<button
											onClick={() => {
												const text = editedConfig || gatewayConfig;
												if (text && onSaveGatewayConfig) {
													onSaveGatewayConfig(text);
												}
											}}
											disabled={savingGatewayConfig || !!jsonError}
											className="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-lg font-medium transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
										>
											{savingGatewayConfig ? (
												<>
													<svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
														<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
														<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
													</svg>
													<span>Saving...</span>
												</>
											) : (
												<span>Save Config</span>
											)}
										</button>
										<button
											onClick={() => {
												setEditedConfig('');
												setJsonError(null);
												if (onFetchGatewayConfig) onFetchGatewayConfig();
											}}
											disabled={loadingGatewayConfig}
											className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50"
										>
											Reload
										</button>
									</div>
								</div>
							);
						})()}

						{/* Info box */}
						<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
							<div className="flex items-start space-x-3">
								<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								<div>
									<p className="text-sm font-medium text-blue-800 dark:text-blue-200">About Advanced Configuration</p>
									<ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-disc list-inside">
										<li>This is the raw OpenClaw configuration for <strong>this instance only</strong></li>
										<li>Each instance has its own isolated config file on the agent</li>
										<li>Changes are synced directly to the agent and the gateway restarts automatically</li>
										<li>Channels managed by ClawdGate's <strong>Protected Integrations</strong> should not be enabled here</li>
										<li>Enabling a channel in both places will cause polling conflicts (e.g. Telegram 409 errors)</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* ══════════════════════ Skills Tab ══════════════════════ */}
				{activeTab === 'skills' && skillsContent && (
					<div>{skillsContent}</div>
				)}

				{/* ══════════════════════ Access Tab ══════════════════════ */}
				{activeTab === 'access' && accessContent && (
					<div>{accessContent}</div>
				)}

				{/* ══════════════════════ Init Tab ══════════════════════ */}
				{activeTab === 'init' && initContent && (
					<div>{initContent}</div>
				)}

				</div>
		</div>
	);
}

// ── Channel Key List helper component (rendered inside integration cards at team level) ──

function ChannelKeyList({
	channelType,
	channelKeys,
	onAdd,
	onEdit,
	onDelete,
	teamAgents,
	onAssign,
}: {
	channelType: string;
	channelKeys: ChannelKey[];
	onAdd: (channelType: string) => void;
	onEdit: (key: ChannelKey) => void;
	onDelete: (keyId: string) => void;
	teamAgents?: TeamAgent[];
	onAssign?: (keyId: string, instanceId: string) => void;
}) {
	const filtered = channelKeys.filter(k => k.channel_type === channelType);

	// Build a flat list of all instances across all agents for the dropdown
	const allInstances: { instance_id: string; instance_name: string; running_status: string; hostname: string }[] = [];
	if (teamAgents) {
		for (const agent of teamAgents) {
			if (agent.instances) {
				for (const inst of agent.instances) {
					allInstances.push({ ...inst, hostname: agent.hostname });
				}
			}
		}
	}

	// Build set of instance_ids already assigned to a key of this channel type
	const assignedInstanceIds = new Set(
		filtered.filter(k => k.assigned_to?.instance_id).map(k => k.assigned_to!.instance_id!)
	);

	// Warn if there are more instances than keys for this channel type
	const unassignedInstanceCount = allInstances.length - filtered.length;

	return (
		<div className="space-y-2">
			<label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Channel Keys</label>
			{unassignedInstanceCount > 0 && filtered.length > 0 && (
				<div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
					<svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
					</svg>
					<p className="text-xs text-amber-800 dark:text-amber-300">
						<span className="font-semibold">{unassignedInstanceCount} instance{unassignedInstanceCount > 1 ? 's' : ''}</span> without a {channelType} key — add more keys or some instances won't receive messages on this channel.
					</p>
				</div>
			)}
			{filtered.length === 0 && allInstances.length > 0 ? (
				<div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
					<svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
					</svg>
					<p className="text-xs text-amber-800 dark:text-amber-300">
						No keys yet — you have <span className="font-semibold">{allInstances.length} instance{allInstances.length > 1 ? 's' : ''}</span> that need {channelType} keys for auto-assignment.
					</p>
				</div>
			) : filtered.length === 0 ? (
				<p className="text-sm text-slate-500 dark:text-slate-400 italic">
					No keys yet — add one to enable auto-assignment
				</p>
			) : (
				<div className="space-y-1.5">
					{filtered.map((key) => (
						<div key={key.key_id} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg">
							<div className="flex items-center space-x-2 min-w-0">
								<span className="text-sm font-medium text-slate-900 dark:text-white truncate">{key.label}</span>
								{key.is_default && (
									<span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex-shrink-0">
										Default
									</span>
								)}
							</div>
							<div className="flex items-center space-x-2 flex-shrink-0">
								{allInstances.length > 0 && onAssign ? (
									<select
										value={key.assigned_to?.instance_id || ''}
										onChange={(e) => {
											if (e.target.value) {
												onAssign(key.key_id, e.target.value);
											}
										}}
										className="text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer"
									>
										<option value="">Unassigned</option>
										{allInstances.map(inst => (
											<option
												key={inst.instance_id}
												value={inst.instance_id}
												disabled={assignedInstanceIds.has(inst.instance_id) && key.assigned_to?.instance_id !== inst.instance_id}
											>
												{inst.hostname} / {inst.instance_name} ({inst.running_status}){assignedInstanceIds.has(inst.instance_id) && key.assigned_to?.instance_id !== inst.instance_id ? ' (has key)' : ''}
											</option>
										))}
									</select>
								) : key.assigned_to ? (
									<span className="text-xs text-slate-500 dark:text-slate-400">
										Assigned to <span className="font-medium text-slate-700 dark:text-slate-200">{key.assigned_to.hostname}{key.assigned_to.instance_name ? ` / ${key.assigned_to.instance_name}` : ''}</span>
									</span>
								) : (
									<span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
										Available
									</span>
								)}
								<button
									onClick={() => onEdit(key)}
									className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium cursor-pointer"
								>
									Edit
								</button>
								<button
									onClick={() => onDelete(key.key_id)}
									className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium cursor-pointer"
								>
									Delete
								</button>
							</div>
						</div>
					))}
				</div>
			)}
			<button
				onClick={() => onAdd(channelType)}
				className="inline-flex items-center space-x-1.5 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium cursor-pointer mt-1"
			>
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
				</svg>
				<span>Add Key</span>
			</button>
		</div>
	);
}

// ── Team Key Banner helper component ──

function TeamKeyBanner({ label }: { label: string }) {
	return (
		<div className="flex items-center space-x-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
			<svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
			</svg>
			<span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
				Using team key: {label}
			</span>
		</div>
	);
}

// ── Integration Card helper component ──

function IntegrationCard({
	name, description, iconBg, icon, enabled, onToggle, children, borderColor, headerBg,
}: {
	name: string;
	description: string;
	iconBg: string;
	icon: React.ReactNode;
	enabled: boolean;
	onToggle: () => void;
	children: React.ReactNode;
	borderColor: string;
	headerBg: string;
}) {
	return (
		<div className={`border ${borderColor} rounded-xl overflow-hidden`}>
			<div className={`flex items-center justify-between p-4 ${headerBg}`}>
				<div className="flex items-center space-x-3">
					<div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>{icon}</div>
					<div>
						<p className="font-medium text-slate-900 dark:text-white">{name}</p>
						<p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
					</div>
				</div>
				<button onClick={onToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${enabled ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'}`}>
					<span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
				</button>
			</div>
			{enabled && (
				<div className={`p-4 space-y-4 border-t ${borderColor}`}>
					{children}
				</div>
			)}
		</div>
	);
}
